import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerClustererF, MarkerF } from '@react-google-maps/api';
import type { Site, SiteType, TypeFilter } from '../types';
import { isEmptySite } from '../filters';
import { clusterIconUrl, siteIcon, type MarkerState } from '../markers';
import { GOOGLE_MAPS_API_KEY, useGoogleMaps } from '../../../hooks/useGoogleMaps';
import TypeMark from './TypeMark';

interface SiteMapProps {
  sites: Site[];
  visibleIds: ReadonlySet<string>;
  selectedId: string | null;
  hoverId: string | null;
  filterKey: string;
  layoutKey: string;
  type: TypeFilter;
  legendCounts: Record<SiteType, number>;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onClearSelection: () => void;
  onType: (type: TypeFilter) => void;
}

/** Rockfall routes hold no stock and are not on the Locations page. */
const LEGEND: { id: SiteType; label: string }[] = [
  { id: 'pit', label: 'Pit' },
  { id: 'yard', label: 'Yard' },
];

/** SA16, before the sites have been fitted. */
const START = { center: { lat: 51.15, lng: -121.4 }, zoom: 7 };
const FIT_MAX_ZOOM = 11;
const FOCUS_ZOOM = 13;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function markerLabel(site: Site): string {
  return site.number ? `${site.name} #${site.number}` : site.name;
}

const plotted = (sites: Site[]) => sites.filter((site) => site.latitude != null && site.longitude != null);

/** Fit the map to the matching sites (all sites if none match), never closer than FIT_MAX_ZOOM. */
function fitTo(map: google.maps.Map, sites: Site[], visibleIds: ReadonlySet<string>): void {
  const all = plotted(sites);
  if (!all.length) return;
  const visible = all.filter((site) => visibleIds.has(site.id));
  const bounds = new google.maps.LatLngBounds();
  for (const site of visible.length ? visible : all) bounds.extend({ lat: site.latitude!, lng: site.longitude! });
  map.fitBounds(bounds, 40);
  google.maps.event.addListenerOnce(map, 'idle', () => {
    if ((map.getZoom() ?? 0) > FIT_MAX_ZOOM) map.setZoom(FIT_MAX_ZOOM);
  });
}

const ZOOM_BUTTON = 'tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-border-0 tw-bg-surface tw-text-lg tw-leading-none tw-text-content hover:tw-bg-surface-hover';

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  // Satellite view earns its place for pits and stockpiles; everything else stays off.
  mapTypeControl: true,
  mapTypeControlOptions: { position: 3 /* google.maps.ControlPosition.TOP_RIGHT */ },
  clickableIcons: false,
  minZoom: 5,
  maxZoom: 18,
  gestureHandling: 'greedy',
};

const CLUSTER_OPTIONS = {
  gridSize: 40,
  // Spread markers rather than cluster once the user is close in.
  maxZoom: 14,
  styles: [{ url: clusterIconUrl(), width: 32, height: 32, textColor: '#ffffff', textSize: 12, fontWeight: '600' }],
};

/** Shown in place of the map when it cannot be drawn. The list beside it keeps working. */
const MapNotice: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
  <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-6" role="status">
    <div className="tw-max-w-sm tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-4 tw-text-center tw-shadow-card">
      <p className="tw-m-0 tw-font-semibold tw-text-content">{title}</p>
      <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-content-muted">{detail}</p>
    </div>
  </div>
);

interface GoogleSiteMapProps extends Omit<SiteMapProps, 'type' | 'legendCounts' | 'onType'> {}

const GoogleSiteMap: React.FC<GoogleSiteMapProps> = ({
  sites,
  visibleIds,
  selectedId,
  hoverId,
  filterKey,
  layoutKey,
  onSelect,
  onHover,
  onClearSelection,
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const visibleRef = useRef(visibleIds);
  visibleRef.current = visibleIds;

  const located = useMemo(() => plotted(sites), [sites]);
  const matching = useMemo(() => located.filter((site) => visibleIds.has(site.id)), [located, visibleIds]);
  const context = useMemo(() => located.filter((site) => !visibleIds.has(site.id)), [located, visibleIds]);

  const stateOf = useCallback(
    (site: Site): MarkerState => ({
      selected: selectedId === site.id,
      hovered: hoverId === site.id,
      empty: isEmptySite(site),
      dimmed: !visibleIds.has(site.id),
    }),
    [selectedId, hoverId, visibleIds]
  );

  // Fit on first load and whenever the filter changes what matches.
  useEffect(() => {
    if (map) fitTo(map, sites, visibleRef.current);
  }, [map, sites, filterKey]);

  // The split pane resizing the map: keep the fitted view centred.
  useEffect(() => {
    if (!map) return;
    const center = map.getCenter();
    const frame = requestAnimationFrame(() => {
      if (center) map.setCenter(center);
    });
    return () => cancelAnimationFrame(frame);
  }, [map, layoutKey]);

  // Bring a selected site into view, close enough to tell it from its neighbours.
  useEffect(() => {
    if (!map || !selectedId) return;
    const site = located.find((item) => item.id === selectedId);
    if (!site) return;
    const at = { lat: site.latitude!, lng: site.longitude! };
    if ((map.getZoom() ?? 0) < 12) map.setZoom(FOCUS_ZOOM);
    if (prefersReducedMotion()) map.setCenter(at);
    else map.panTo(at);
  }, [map, selectedId, located]);

  if (loadError) {
    return <MapNotice title="The map could not load" detail="Google Maps did not respond. The site list still works." />;
  }
  if (!isLoaded) {
    return <MapNotice title="Loading map…" detail="Plotting the sites." />;
  }

  const marker = (site: Site, clusterer?: unknown) => {
    const state = stateOf(site);
    return (
      <MarkerF
        key={site.id}
        position={{ lat: site.latitude!, lng: site.longitude! }}
        icon={siteIcon(site.type, state)}
        title={markerLabel(site)}
        // A filtered-out site stays visible for context but cannot be picked.
        clickable={!state.dimmed}
        opacity={state.dimmed ? 0.28 : 1}
        zIndex={state.selected || state.hovered ? 800 : state.dimmed ? 0 : 10}
        clusterer={clusterer as never}
        noClustererRedraw={Boolean(clusterer)}
        onClick={() => onSelect(site.id)}
        onMouseOver={() => onHover(site.id)}
        onMouseOut={() => onHover(null)}
      />
    );
  };

  return (
    <>
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        center={START.center}
        zoom={START.zoom}
        options={MAP_OPTIONS}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        onClick={onClearSelection}
      >
        {/* Filtered-out sites stay as individual dim pins, so a cluster never counts only context. */}
        {context.map((site) => marker(site))}
        <MarkerClustererF options={CLUSTER_OPTIONS}>
          {(clusterer) => <>{matching.map((site) => marker(site, clusterer))}</>}
        </MarkerClustererF>
      </GoogleMap>
      {map && (
        <div className="tw-absolute tw-left-3 tw-top-3 tw-z-10 tw-flex tw-flex-col tw-divide-y tw-divide-line tw-overflow-hidden tw-rounded-lg tw-bg-surface tw-shadow-md">
          <button type="button" className={ZOOM_BUTTON} aria-label="Zoom in" onClick={() => map.setZoom((map.getZoom() ?? START.zoom) + 1)}>
            +
          </button>
          <button type="button" className={ZOOM_BUTTON} aria-label="Zoom out" onClick={() => map.setZoom((map.getZoom() ?? START.zoom) - 1)}>
            −
          </button>
          <button
            type="button"
            className={ZOOM_BUTTON}
            aria-label="Fit all visible sites"
            title="Fit all visible sites"
            onClick={() => fitTo(map, sites, visibleIds)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

const SiteMap: React.FC<SiteMapProps> = ({ type, legendCounts, onType, ...mapProps }) => {
  // Checked before the loader starts: without a key it would fail loudly in the console.
  const hasKey = GOOGLE_MAPS_API_KEY.trim().length > 0;
  return (
    <section className="tw-relative tw-h-full tw-min-h-0 tw-w-full tw-bg-surface-alt" aria-label="Map of sites">
      {hasKey ? (
        <GoogleSiteMap {...mapProps} />
      ) : (
        <MapNotice
          title="Map unavailable"
          detail="No Google Maps key is configured for this environment. The site list still works."
        />
      )}
      <div className="tw-absolute tw-bottom-7 tw-left-3 tw-z-10 tw-flex tw-items-center tw-gap-0.5 tw-rounded-lg tw-bg-surface tw-p-1 tw-text-sm tw-shadow-md">
        {LEGEND.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={type === item.id}
            onClick={() => onType(type === item.id ? 'all' : item.id)}
            className={`tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1 tw-font-medium tw-text-content hover:tw-bg-surface-hover ${
              type === item.id ? 'tw-bg-surface-hover tw-ring-1 tw-ring-line' : 'tw-bg-transparent'
            }`}
          >
            <TypeMark type={item.id} />
            {item.label}
            <span className="tw-font-normal tw-tabular-nums tw-text-content-muted">{legendCounts[item.id]}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

export default SiteMap;
